import { useState } from 'react';
import { uploadFile } from '../services/image.service';
import { takePicture, dataUrlToFile } from './common';

interface UseImageUploadOptions {
    onSuccess?: (responseBody: any) => void;
    onError?: (error: string) => void;
    primaryId: string;
    secondaryId: string;
    validateBeforeUpload?: () => boolean | string; // Returns true if valid, or error message if invalid
}

interface UseImageUploadReturn {
    isUploading: boolean;
    uploadImage: () => Promise<void>;
    error: string | null;
    clearError: () => void;
}

export const useImageUpload = ({
    onSuccess,
    onError,
    primaryId,
    secondaryId,
    validateBeforeUpload
}: UseImageUploadOptions): UseImageUploadReturn => {
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const clearError = () => setError(null);

    const uploadImage = async (): Promise<void> => {
        if (isUploading) return; // Prevent multiple uploads

        try {
            setIsUploading(true);
            setError(null);

            // Validate before proceeding with upload
            if (validateBeforeUpload) {
                const validationResult = validateBeforeUpload();
                if (validationResult !== true) {
                    const errorMessage = typeof validationResult === 'string' 
                        ? validationResult 
                        : 'Please complete required fields before uploading image';
                    setError(errorMessage);
                    if (onError) {
                        onError(errorMessage);
                    }
                    return;
                }
            }

            const base64Image = await takePicture();
            if (base64Image) {
                const imageFile = await dataUrlToFile(base64Image);
                const uploadResponse = await uploadFile(primaryId, secondaryId, imageFile);
                const responseBody = await uploadResponse.json();
                
                if (onSuccess) {
                    onSuccess(responseBody);
                }
            }
        } catch (err: any) {
            const errorMessage = err.message || 'Failed to upload image';
            setError(errorMessage);
            
            if (onError) {
                onError(errorMessage);
            }
            
            console.error('Image upload error:', err);
        } finally {
            setIsUploading(false);
        }
    };

    return {
        isUploading,
        uploadImage,
        error,
        clearError
    };
};

export default useImageUpload;
